import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Shield, Award, Globe, Cpu, Sparkles } from "lucide-react";
import { STATS, TIMELINE } from "../data/site";
import Reveal from "../components/Reveal";
import PageHero from "../components/PageHero";

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About UMD"
        title={<>Forty years of<br /><span className="text-[var(--primary)]">engineering Australia.</span></>}
        subtitle="From a Melbourne workshop in 1983 to one of the country's most trusted industrial engineering practices — UMD is a story of relentless problem-solving."
        image="https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&w=1920&q=80"
      />

      {/* Our Story */}
      <section className="py-24 md:py-32 container-wide grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <div className="eyebrow mb-6">Our story</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">An engineering practice — never an integrator.</h2>
        </Reveal>
        <Reveal className="md:col-span-6 md:col-start-7" delay={150}>
          <div className="space-y-6 text-base leading-relaxed text-[#333]">
            <p>UMD was founded in 1983 by engineers who believed Australia needed a serious, independent technology practice — not a reseller of foreign hardware. That founding conviction has shaped every decision since.</p>
            <p>Today, UMD designs and manufactures IoT devices, integrates complex enterprise systems and provides engineering services across electronics, IT, software and lifecycle support. We operate from our Bentleigh East facility and deploy nationwide.</p>
            <p>What hasn't changed is our position. We are still — first and foremost — engineers who solve problems other people walk away from.</p>
          </div>
        </Reveal>
      </section>

      {/* Mission Values */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="container-wide relative grid md:grid-cols-3 gap-px bg-white/10">
          {[
            { icon: Sparkles, t: "Mission", d: "To engineer the systems that critical Australian industries depend on — built locally, deployed nationally, supported for life." },
            { icon: Award, t: "Vision", d: "Australia's most trusted independent engineering practice for IoT, edge and mission-critical industrial systems." },
            { icon: Shield, t: "Values", d: "Engineering rigor. Long-term partnerships. A can-do attitude. Solving the problem properly the first time." },
          ].map((v, i) => (
            <Reveal key={i} delay={i * 80} className="bg-[#0a0a0a] p-10 md:p-14">
              <v.icon size={32} className="text-[var(--primary)] mb-8" strokeWidth={1.4} />
              <h3 className="font-display text-3xl tracking-tight">{v.t}</h3>
              <p className="text-white/70 mt-4 leading-relaxed">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-32 container-wide">
        <div className="grid md:grid-cols-4 gap-px bg-[var(--border)]">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 70} className="bg-white p-8 md:p-10">
              <div className="stat-number text-[var(--primary)]">{s.value}</div>
              <div className="mt-4 text-sm font-semibold">{s.label}</div>
              <div className="text-xs text-[var(--muted-fg)] mt-1 font-mono-tech tracking-wider uppercase">{s.sub}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32 bg-[#fafafa]">
        <div className="container-wide">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <Reveal className="md:col-span-5">
              <div className="eyebrow mb-6">Milestones</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">Four decades.<br/>One trajectory.</h2>
            </Reveal>
          </div>

          <div className="relative">
            <div className="absolute left-0 right-0 top-[28px] h-px bg-[var(--border)] hidden md:block" />
            <div className="grid md:grid-cols-6 gap-10">
              {TIMELINE.map((m, i) => (
                <Reveal key={i} delay={i * 100} className="relative">
                  <div className="hidden md:block w-3 h-3 bg-[var(--primary)] -translate-y-[20px] mb-6"/>
                  <div className="font-display text-3xl text-[var(--primary)] tracking-tight">{m.year}</div>
                  <div className="font-semibold mt-2">{m.title}</div>
                  <p className="text-sm text-[var(--muted-fg)] mt-3 leading-relaxed">{m.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-24 md:py-32 container-wide">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="eyebrow mb-6">Safety & compliance</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">Compliance is engineered in — not bolted on.</h2>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7 space-y-4">
            {[
              "ISO 9001 — Quality Management Systems",
              "ISO 27001 — Information Security",
              "AS/NZS 3000 — Wiring Rules compliance",
              "AS/NZS 4801 — Occupational Health & Safety",
              "Australian Industry Group accredited member",
              "Select Industrial Automation Partner (Schneider Electric)",
            ].map((c, i) => (
              <Reveal key={i} delay={i * 60} className="flex items-start gap-4 py-4 border-b border-[var(--border)]">
                <CheckCircle2 size={20} className="text-[var(--primary)] mt-1 shrink-0" />
                <span className="text-base">{c}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}

export const CtaBlock = () => (
  <section className="py-24 md:py-32 bg-[#08142b] text-white relative overflow-hidden">
    <div className="grid-bg absolute inset-0 opacity-50" />
    <div className="container-wide relative grid md:grid-cols-12 gap-12 items-end">
      <div className="md:col-span-8">
        <div className="eyebrow eyebrow-light mb-6">Start a conversation</div>
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">Ready to engineer<br/>something <span className="text-[var(--primary)]">critical?</span></h2>
      </div>
      <div className="md:col-span-4">
        <Link to="/contact" data-testid="page-cta-contact" className="btn-primary justify-between w-full">Get a proposal <ArrowUpRight size={18} className="arrow"/></Link>
      </div>
    </div>
  </section>
);
